/* eslint-disable react/no-danger */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';

import {
  PrivacySettingToggle,
  PrivacySettingToggles,
} from '@prisjakt/web-app-config/types/market';
import { noop } from '@prisjakt/ui-base';
import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import { ViewportContext } from '../viewport';
import CtxLink from '../render-context/ctx-link';

import acceptCookiesAction from './actions';
import actions from '../../areas/user/actions/tracking';

import { AppState } from '../../reducers';

import { trackCookieBannerIsVisible } from './tracking';

import * as S from './styles';

type OwnProps = {};

type StateProps = {
  version: string;
  settings: PrivacySettingToggle;
  isLoggedIn: boolean;
};

type DispatchProps = {
  acceptCookies: (version: string) => void;
  trackingToggleSave: (
    settings: PrivacySettingToggle,
    isLoggedIn: boolean
  ) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

/**
  Cookie banner reads state from localstorage and/or cookie.
  Because of this we cannot render the cookie banner server side
  as usual since users with previous accepts will get a "flash-
  of-already-accepted-cookie-banner".

  The componentDidMount makes sure cookie banner only renders client side
* */
const CookieBanner = ({
  version,
  acceptCookies = noop,
  trackingToggleSave,
  settings,
  isLoggedIn,
}: Props) => {
  const [isVisible, setVisibilityState] = useState(false);
  const { t, renderMode, market } = useContext(AppContext);
  const cdn = market && market.cdn ? market.cdn : 'https://cdn.pji.nu';
  const { isDesktop } = useContext(ViewportContext);
  const privacySettings =
    market &&
    market.privacySettings &&
    (market.privacySettings[renderMode] || market.privacySettings.responsive);

  useEffect(() => {
    setVisibilityState(true);
  }, []);

  useEffect(() => {
    trackCookieBannerIsVisible(
      Boolean(!window.SESSION_DATA.session.cookie_consent_version)
    );
  }, []);

  const onAcceptCookies = useCallback(() => {
    trackingToggleSave(
      {
        ...settings,
        [PrivacySettingToggles.BehaviorTrackingMarketing]: true,
      },
      isLoggedIn
    );
    acceptCookies(privacySettings.cookieVersion);
  }, [
    isLoggedIn,
    privacySettings,
    acceptCookies,
    settings,
    trackingToggleSave,
  ]);

  if (!isVisible) return null;
  if (renderMode === 'native') return null;

  if (version === privacySettings.cookieVersion) return null;

  const rootElement = document.getElementById('root-cookiebanner');

  const renderButton = () => (
    <S.ButtonsWrapper>
      <S.AcceptButton color="primary" onClick={onAcceptCookies}>
        <T domain="common">cookie-banner.accept-button</T>
      </S.AcceptButton>
      <S.Button
        href={privacySettings.privacySettingsUrl}
        color="tertiary"
        variant="outlined"
      >
        {t('common', 'cookie-banner.privacy-policy-settings')}
      </S.Button>
    </S.ButtonsWrapper>
  );

  const renderText = () => (
    <T
      domain="common"
      cookiePolicyLink={
        <CtxLink to={privacySettings.cookieUrl} key="cookie">
          {t('common', 'cookie-banner.cookie-policy')}
        </CtxLink>
      }
      privacyPolicyLink={
        <CtxLink to={privacySettings.privacyUrl} key="privacy">
          {t('common', 'cookie-banner.privacy-policy')}
        </CtxLink>
      }
    >
      cookie-banner.description
    </T>
  );

  const renderLogo = () => (
    <S.LogoWrapper>
      <S.Logo>
        <img
          alt="Schibsted"
          src={`${cdn}/g/rfe/logos/schibsted/schibsted_next_white.png`}
          width="116"
        />
      </S.Logo>
    </S.LogoWrapper>
  );

  return rootElement
    ? createPortal(
        <S.Banner id="cookie-banner" data-test="CookieBanner" isVisible>
          <S.BannerContent>
            <S.BannerText>
              {!isDesktop && renderLogo()}
              {renderText()}
            </S.BannerText>
            {renderButton()}
            {isDesktop && renderLogo()}
          </S.BannerContent>
        </S.Banner>,
        rootElement
      )
    : null;
};

const mapStateToProps = (state: AppState) => ({
  version: state.globals.cookieBanner.version,
  settings: state.areas.user.tracking.settings,
  isLoggedIn: state.areas.account.isAuthenticated,
});

const dispatchProps = {
  acceptCookies: acceptCookiesAction,
  trackingToggleSave: actions.trackingToggleSave,
};

export default connect(mapStateToProps, dispatchProps)(CookieBanner);
